// Единая ссылка-тег на сущность. Раньше такие ссылки писались на каждой странице
// со своими цветами (#d97706, #44403c, text-amber-600) и своими подписями —
// отсюда разный вид одной и той же связи в гридах заметок, встреч, задач.
import type { CSSProperties, ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { Tag, Typography } from 'antd';
import { useTranslation } from '@govard/i18n-core/react';

import { formatPersonName } from '../format/index.js';

export type EntityKind =
  | 'deal'
  | 'contact'
  | 'company'
  | 'task'
  | 'project'
  | 'document'
  | 'meeting'
  | 'template';

// Token-styled kinds: theme-aware chips with AA contrast in both themes.
// Deal is the hero entity in Warm Precision — translucent amber pill
// (accent-text on accent-soft) like the spec's chat-native entity chips.
const KIND_TOKEN_STYLE: Partial<Record<EntityKind, CSSProperties>> = {
  deal: {
    color: 'var(--ant-color-primary-text)',
    background: 'var(--ant-color-primary-bg)',
    borderColor: 'var(--ant-color-primary-border)',
  },
  contact: {
    color: 'var(--ant-color-success-text)',
    background: 'var(--ant-color-success-bg)',
    borderColor: 'var(--ant-color-success-border)',
  },
  company: {
    color: 'var(--ant-color-info-text)',
    background: 'var(--ant-color-info-bg)',
    borderColor: 'var(--ant-color-info-border)',
  },
};

const KIND_COLOR: Record<EntityKind, string> = {
  deal: 'default', // unused: KIND_TOKEN_STYLE takes precedence
  contact: 'green', // unused: KIND_TOKEN_STYLE takes precedence
  company: 'default', // unused: KIND_TOKEN_STYLE takes precedence
  task: 'orange',
  project: 'purple',
  document: 'volcano',
  meeting: 'gold',
  template: 'gold',
};

/** Маршрут карточки сущности. */
function entityPath(kind: EntityKind, id: string): string | null {
  switch (kind) {
    case 'deal':
      return `/deals/${id}`;
    case 'contact':
      return `/contacts/${id}`;
    case 'company':
      return `/companies/${id}`;
    case 'project':
      return `/projects/${id}`;
    case 'document':
      return `/documents/${id}`;
    case 'task':
      return `/tasks/${id}`;
    // У встречи и шаблона нет отдельной карточки — они правятся из своих списков,
    // поэтому ссылка ведёт в раздел, а не на несуществующий маршрут.
    case 'meeting':
      return `/meetings/${id}`;
    case 'template':
      return '/templates';
  }
}

interface EntityLinkProps {
  kind: EntityKind;
  id: string;
  label: string;
  /** Показать тип («Сделка: …») — нужно там, где в одной колонке разные сущности. */
  withKindPrefix?: boolean;
  /** Тегом (для колонок «привязка») или простой ссылкой (для колонки-названия). */
  asTag?: boolean;
  /** Строка кликабельна целиком — не даём ей перехватить переход по ссылке. */
  stopPropagation?: boolean;
}

export function EntityLink({
  kind,
  id,
  label,
  withKindPrefix = false,
  asTag = true,
  stopPropagation = true,
}: EntityLinkProps): ReactElement {
  const { t } = useTranslation();
  const text = withKindPrefix ? `${t(`crm.shared.entity_type.${kind}`)}: ${label}` : label;
  const path = entityPath(kind, id);
  const tokenStyle = KIND_TOKEN_STYLE[kind];
  const tagProps = tokenStyle !== undefined ? { style: tokenStyle } : { color: KIND_COLOR[kind] };
  const content = asTag ? <Tag {...tagProps}>{text}</Tag> : <span>{text}</span>;

  if (!path) {
    return asTag ? content : <span className="text-gray-700">{text}</span>;
  }

  return (
    <Link
      to={path}
      style={asTag ? undefined : { color: 'var(--ant-color-primary)', fontWeight: 600 }}
      onClick={stopPropagation ? (event) => event.stopPropagation() : undefined}
    >
      {content}
    </Link>
  );
}

/** Пустая ячейка связи — одинаковый вид «нет привязки» во всех гридах. */
export function EmptyLinkCell({ text = '—' }: { text?: string }): ReactElement {
  return <Typography.Text type="secondary">{text}</Typography.Text>;
}

/** Готовая ячейка для контакта: имя собирается единообразно. */
export function ContactLinkCell({
  contact,
}: {
  contact?: { id: string; firstName: string; lastName?: string | null } | null;
}): ReactElement {
  if (!contact) return <EmptyLinkCell />;
  return (
    <EntityLink
      kind="contact"
      id={contact.id}
      label={formatPersonName(contact.firstName, contact.lastName)}
      asTag={false}
    />
  );
}
